import { inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { GroupService } from '../groups/group.service';
import { UserService } from '../users-database/user.service';
import { FormService } from '../../../../../form-builder/services/form/form.service';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../..//src/ui-components/services/toast/toast.service';
import { Meta } from '../../../../common/models/api-response.model';
import { Option } from '../../../../common/models/option.model';

export abstract class BaseAssignedList {
  route = inject(ActivatedRoute);
  formService = inject(FormService);
  userService = inject(UserService);
  groupService = inject(GroupService);
  modalService = inject(ModalServiceService);
  router = inject(Router);
  transloco = inject(TranslocoService);
  toaster = inject(ToasterService);

  currentPage = signal<number>(1);
  totalPages = signal<number>(1);
  isLoadingGroupUsers = signal<boolean>(false);
  lookupList = signal<Option[]>([]); // All users
  assignedList = signal<Option[]>([]); // Assigned users (Card List)
  selectedList = signal<Option[]>([]); // Selected users (Assignment Modal)
  addedList = signal<Option[]>([]); // Added users
  removedList = signal<Option[]>([]); // Removed users
  isAssignedListLoading = signal<boolean>(false);
  assignedListCurrentPage = signal<number>(1);
  assignedListTotalPages = signal<number>(1);
  assignedListTotal = signal<number>(0);

  isLoading = signal<boolean>(true);
  pageTitle = signal<string>('');
  editMode = signal<boolean>(false);
  buttonText = signal<string>('');
  isFormChanged = signal<boolean>(false);
  initialFormValues: any = {};

  handleAssignedListResponse(meta: Meta) {
    this.assignedListCurrentPage.set(meta.current_page);
    this.assignedListTotal.set(
      meta.total + this.addedList().length - this.removedList().length
    );
    this.assignedListTotalPages.set(meta.last_page);

    if (this.assignedListCurrentPage() === 1) {
      this.assignedList().unshift(...this.addedList());
    }
    this.assignedList.set(
      this.assignedList().filter(
        item =>
          !this.removedList().some(
            card => card.value.toString() === item.value.toString()
          )
      )
    );

    this.isAssignedListLoading.set(false);
  }

  handleAssignedListFormData(formData: FormData, source?: 'user' | 'group') {
    if (this.assignedList().length) {
      this.assignedList().forEach(card => {
        if (card.isSelected === true) {
          formData.append(
            source === 'group' ? 'added_groups[]' : 'added_users[]',
            card.value.toString()
          );
        }
      });
    }

    if (this.removedList().length) {
      this.removedList().forEach(card => {
        formData.append(
          source === 'group' ? 'removed_groups[]' : 'removed_users[]',
          card.value.toString()
        );
      });
    }
  }

  handleUsersLookupResponse(
    response: { data: Option[]; meta: Meta },
    name: string
  ) {
    if (this.currentPage() === 1) {
      this.lookupList.set(response.data);
    } else {
      this.lookupList().push(...response.data);
    }

    // remove selected users from the users lookup list
    this.removeSelectedListFromLookupList();
    // remove added users from the users lookup list
    this.removeAddedListFromLookupList();
    // add removed users to the users lookup list
    this.addRemovedListFromLookupList(name);

    this.totalPages.set(response.meta.last_page);
    this.isLoadingGroupUsers.set(false);
  }

  removeSelectedListFromLookupList() {
    if (this.selectedList().length) {
      this.lookupList.set(
        this.lookupList().filter(
          item =>
            !this.selectedList().some(
              card => card.value.toString() === item.value.toString()
            )
        )
      );
    }
  }

  removeAddedListFromLookupList() {
    if (this.addedList().length) {
      this.lookupList.set(
        this.lookupList().filter(
          item =>
            !this.addedList().some(
              card => card.value.toString() === item.value.toString()
            )
        )
      );
    }
  }

  addRemovedListFromLookupList(name: string) {
    if (this.removedList().length) {
      if (name.length >= 3) {
        const list = [
          ...this.lookupList(),
          ...this.removedList().filter(item =>
            item.label.toLocaleLowerCase().includes(name.toLocaleLowerCase())
          ),
        ];
        this.lookupList.set(list);
      } else {
        this.lookupList().push(...this.removedList());
      }
    }
  }

  baseSelectItem(item: Option) {
    item.isSelected = true;
    this.selectedList().unshift(item);

    // remove the selected item from the lookup list to avoid duplication
    this.lookupList.set(
      this.lookupList().filter(card => card.value !== item.value)
    );
  }

  removeSelectedItem(item: Option) {
    item.isSelected = false;
    this.selectedList.set(
      this.selectedList().filter(card => card.value !== item.value)
    );

    this.lookupList().unshift(item);
  }

  removeAssignedItem(item: Option) {
    this.assignedList.set(
      this.assignedList().filter(card => card.value !== item.value)
    );

    if (item.isSelected === true) {
      this.addedList.set(
        this.addedList().filter(card => card.value !== item.value)
      );
    } else {
      this.removedList().push(item);
    }
    this.assignedListTotal.set(this.assignedListTotal() - 1);

    this.lookupList().unshift(item);

    if (this.editMode()) {
      this.isFormChanged.set(true);
    }
  }

  saveAssignedItems() {
    const filteredRemovedList = this.removedList().length
      ? this.removedList().filter(item =>
          this.selectedList().some(card => card.value == item.value)
        )
      : [];

    if (filteredRemovedList.length) {
      this.removedList.set(
        this.removedList().filter(
          item => !this.selectedList().some(card => card.value == item.value)
        )
      );

      this.selectedList().forEach(item => {
        if (filteredRemovedList.some(card => card.value == item.value)) {
          item.isSelected = false;
        }
      });
    } else {
      this.addedList().unshift(...this.selectedList());
    }

    if (this.assignedListCurrentPage() !== 1) {
      this.assignedListCurrentPage.set(1);
      this.getAssignedCardList();
    }

    this.assignedList().unshift(
      ...this.selectedList().sort((a, b) => a.label.localeCompare(b.label))
    );

    this.assignedListTotal.set(
      this.assignedListTotal() +
        this.addedList().length -
        this.removedList().length
    );

    if (this.editMode()) {
      this.isFormChanged.set(true);
    }
  }

  handleSuccess() {
    if (this.editMode()) {
      this.navigateToDetails();
    } else {
      this.navigateToList();
    }
    this.isLoading.set(false);
  }

  goBack() {
    history.back();
  }

  protected abstract getAssignedCardList(): void;
  protected abstract navigateToList(): void;
  protected abstract navigateToDetails(): void;
}
